// @flow
import * as React from 'react';

import { AsyncBtn, ContentContainer, SaveText, SaveTextWrapper } from './styles';
import type { AsyncButtonMessages, AsyncButtonProps } from '~/core/types';

import ButtonProgress from './progress';
import { debounce, noop } from 'lodash/fp';
import defaultMessages from '~/core/i18n/components/widgets/buttons/async/intl';
import { FormattedMessage } from 'react-intl';
import type { SaveState } from '~/core/constants/components/widgets/buttons/async';
import { saveStates } from '~/core/constants/components/widgets/buttons/async';

// Helpers
const getSaveTextStates = (messages: AsyncButtonMessages): { [SaveState]: $npm$ReactIntl$MessageDescriptor } => {
    const nextMessages = {
        ...defaultMessages,
        ...messages
    };

    return {
        [saveStates.DEFAULT]: nextMessages.save,
        [saveStates.SAVING]: nextMessages.saving,
        [saveStates.SAVED]: nextMessages.saved,
        [saveStates.SAVED_END]: nextMessages.save,
        [saveStates.SAVE_ERROR]: nextMessages.error,
        [saveStates.SAVE_ERROR_END]: nextMessages.error
    };
};

const delayedSetStateShort = debounce(1000, setState => setState());
const delayedSetStateLong = debounce(2000, setState => setState());
const delayedSetStateForError = debounce(2500, setState => setState());

// Component
type State = {
    saveState: SaveState
};

class AsyncButton extends React.Component<AsyncButtonProps, State> {
    _mounted: boolean = false;

    state = {
        saveState: saveStates.DEFAULT
    };

    componentDidMount() {
        this._mounted = true;
    }

    componentWillReceiveProps(nextProps: AsyncButtonProps) {
        const { saving, error } = nextProps;
        const { saving: lastSaving, onAfterAnimation: afterAnimation = noop } = this.props;

        if (saving) {
            this.setState({ saveState: saveStates.SAVING });
        } else if (!saving && lastSaving && !error) {
            this.setState({ saveState: saveStates.SAVED });
            delayedSetStateShort(() => {
                // To prevent setState on unMounted component err
                if (!this._mounted) {
                    return;
                }

                this.setState({ saveState: saveStates.SAVED_END });
                afterAnimation({ saveState: saveStates.SAVED_END });
            });
            delayedSetStateLong(() => {
                if (!this._mounted) {
                    return;
                }

                this.setState({ saveState: saveStates.DEFAULT });
                afterAnimation({ saveState: saveStates.DEFAULT });
            });
        } else if (error) {
            this.setState({ saveState: saveStates.SAVE_ERROR });
            delayedSetStateLong(() => {
                this.setState({ saveState: saveStates.SAVE_ERROR_END });
                afterAnimation({ saveState: saveStates.SAVE_ERROR_END });
            });
            delayedSetStateForError(() => {
                this.setState({ saveState: saveStates.DEFAULT });
                afterAnimation({ saveState: saveStates.DEFAULT });
            });
        }
    }

    componentWillUnmount() {
        this._mounted = false;
    }

    onClick = () => {
        const { disabled, onClick } = this.props;
        const { saveState } = this.state;

        if (disabled || saveState !== saveStates.DEFAULT) {
            return;
        }

        if (onClick) {
            onClick();
        }
    };

    render() {
        const { disabled, messages = defaultMessages, values, ...rest } = this.props;
        const { saveState } = this.state;

        const saveTextStates = getSaveTextStates(messages);

        return (
            <div>
                <AsyncBtn disabled={disabled} onClick={this.onClick} saveState={saveState} {...rest}>
                    <ContentContainer>
                        <ButtonProgress saveState={saveState} />
                        <SaveTextWrapper>
                            {Object.keys(saveStates).map(key => (
                                <SaveText key={key} saveState={saveState} thisState={saveStates[key]}>
                                    <FormattedMessage {...saveTextStates[key]} values={values} />
                                </SaveText>
                            ))}
                        </SaveTextWrapper>
                    </ContentContainer>
                </AsyncBtn>
            </div>
        );
    }
}
export default AsyncButton;



// WEBPACK FOOTER //
// ./src/components/widgets/buttons/async/index.js